import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { scheduleApi, type ScheduleRollupRow, type ScheduleHealth } from '../../lib/apiClient';
import { StateView, Badge } from '../../components';
import { fmtDate } from '../../lib/dates';
import type { BadgeTone } from '../../components';

// Health dot + label + badge tone, matching the project workspace's HEALTH map.
const HEALTH: Record<ScheduleHealth, { label: string; dot: string; tone: BadgeTone }> = {
  'on-track':  { label: 'On Track',  dot: 'var(--ew-green)',  tone: 'success' },
  'at-risk':   { label: 'At Risk',   dot: 'var(--ew-safety)', tone: 'warning' },
  'off-track': { label: 'Off Track', dot: 'var(--ew-danger)', tone: 'danger'  },
};

// fmtDate from lib/dates: milestone finishes are calendar dates — naive
// new Date() parsing shifted them back a day in Eastern time.

// Standalone-page styles. The .pw-* classes used in the workspace are injected
// by ProjectWorkspace and aren't in scope on a top-level route, so this page
// carries its own.
const css = `
  .rollup-scroll { max-width: 1100px; margin: 0 auto; }
  .rollup-eyebrow {
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-400);
  }
  .rollup-title {
    font: var(--ew-fw-semibold) 28px/1.15 var(--ew-font); letter-spacing: -0.01em;
    color: var(--ew-dark); margin: 6px 0 var(--ew-s-5);
  }
  .rollup-card { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); }
  .rollup-card-hd {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); margin: 0; padding: 14px var(--ew-s-4);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .rollup-row {
    display: grid; grid-template-columns: 1fr 180px 120px; gap: var(--ew-s-4); align-items: center;
    padding: 12px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); cursor: pointer;
    transition: background var(--ew-dur-fast) var(--ew-ease); text-align: left; width: 100%;
    background: transparent; border-left: 0; border-right: 0; border-top: 0; font-family: var(--ew-font);
  }
  .rollup-row:last-child { border-bottom: 0; }
  .rollup-row:hover { background: var(--ew-n-50); }
  .rollup-job { font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.2 var(--ew-font); color: var(--ew-dark); }
  .rollup-sub { font: var(--ew-fw-regular) var(--ew-fs-meta)/1.3 var(--ew-font); color: var(--ew-n-500); margin-top: 3px; }
  .rollup-bar-track { height: 6px; border-radius: var(--ew-r-pill); background: var(--ew-n-100); overflow: hidden; }
  .rollup-bar-fill { height: 100%; border-radius: var(--ew-r-pill); background: var(--ew-performance); }
  .rollup-bar-pct { font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font); color: var(--ew-n-500); margin-top: 5px; font-variant-numeric: tabular-nums; }
  .rollup-health { display: flex; align-items: center; gap: 7px; justify-content: flex-end; }
  .rollup-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
`;

export default function ScheduleRollup() {
  const navigate = useNavigate();
  const query = useQuery<ScheduleRollupRow[]>({
    queryKey: ['schedule-rollup'],
    queryFn: () => scheduleApi.rollup(),
    staleTime: 60_000,
  });

  return (
    <>
      <style>{css}</style>
      <div className="rollup-scroll">
        <div className="rollup-eyebrow">Company-wide</div>
        <h1 className="rollup-title">Schedule Rollup</h1>

        <div className="rollup-card">
          <h2 className="rollup-card-hd">Critical milestones by job</h2>
          <StateView query={query} empty="No schedules imported yet.">
            {(rows) => (
              <>
                {rows.map((r) => {
                  const h = HEALTH[r.health] ?? HEALTH['on-track'];
                  const pct = Math.max(0, Math.min(100, r.pctComplete));
                  return (
                    <button
                      key={r.projectId}
                      type="button"
                      className="rollup-row"
                      onClick={() => navigate(`/projects/${r.projectId}?tab=schedule`)}
                    >
                      <div>
                        <div className="rollup-job">{r.jobNumber} · {r.title}</div>
                        <div className="rollup-sub">
                          {r.nextMilestone
                            ? <>Next: {r.nextMilestone.name} · {fmtDate(r.nextMilestone.finish)}</>
                            : 'No upcoming milestone'}
                        </div>
                      </div>
                      <div>
                        <div className="rollup-bar-track">
                          <div className="rollup-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="rollup-bar-pct">{pct}% complete · {r.activityCount} tasks</div>
                      </div>
                      <div className="rollup-health">
                        <span className="rollup-dot" style={{ background: h.dot }} />
                        <Badge label={h.label} tone={h.tone} />
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </StateView>
        </div>
      </div>
    </>
  );
}
